/**
 * PicksFilterBar Component
 * Filter and sort controls for pick results
 */

import { getCategoryLabel, PROP_TYPE_FILTER_OPTIONS } from '@parlay-picker/shared/propTypes';
import { ArrowDown, ArrowUp, Search } from 'lucide-react';
import type React from 'react';
import type { ChangeEvent } from 'react';
import type { PicksFilter } from '../constants';

// =============================================================================
// Types
// =============================================================================

interface PicksFilterBarProps {
  filters: PicksFilter;
  onFiltersChange: (filters: PicksFilter) => void;
  totalCount: number;
  filteredCount: number;
  onQuickStatsClick?: () => void;
}

// =============================================================================
// Component
// =============================================================================

export function PicksFilterBar({
  filters,
  onFiltersChange,
  totalCount,
  filteredCount,
  onQuickStatsClick,
}: PicksFilterBarProps): React.ReactElement {
  const hasActiveFilters =
    filters.confidence !== 'all' ||
    filters.propType !== 'all' ||
    filters.playerSearch.trim() !== '' ||
    filters.sortBy !== 'default' ||
    filters.sortDirection !== 'desc';

  const clearFilters = (): void => {
    onFiltersChange({
      confidence: 'all',
      propType: 'all',
      playerSearch: '',
      sortBy: 'default',
      sortDirection: 'desc',
    });
  };

  const updateFilter = (key: keyof PicksFilter, value: string): void => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <div className="bg-theme-surface rounded-lg p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        {/* Confidence Filter */}
        <div className="flex items-center gap-2">
          <label className="text-xs text-theme-text-secondary">Confidence:</label>
          <div className="relative">
            <select
              value={filters.confidence}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                updateFilter('confidence', e.target.value)
              }
              className="bg-theme-surface-hover border border-theme-border rounded px-2 py-2 sm:py-1 pr-6 text-sm text-white focus:outline-none focus:border-theme-primary"
            >
              <option value="all">All</option>
              <option value="high">High</option>
              <option value="moderate">Moderate</option>
              <option value="low">Low</option>
            </select>
            {filters.confidence !== 'all' && (
              <button
                type="button"
                onClick={() => updateFilter('confidence', 'all')}
                className="absolute right-1 top-1/2 -translate-y-1/2 text-theme-text-secondary hover:text-white text-xs"
                title="Clear"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Prop Type Filter */}
        <div className="flex items-center gap-2">
          <label className="text-xs text-theme-text-secondary">Prop Type:</label>
          <div className="relative">
            <select
              value={filters.propType}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                updateFilter('propType', e.target.value)
              }
              className="bg-theme-surface-hover border border-theme-border rounded px-2 py-2 sm:py-1 pr-6 text-sm text-white focus:outline-none focus:border-theme-primary"
            >
              <option value="all">All</option>
              {PROP_TYPE_FILTER_OPTIONS.filter((o) => o.key !== '').map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            {filters.propType !== 'all' && (
              <button
                type="button"
                onClick={() => updateFilter('propType', 'all')}
                className="absolute right-1 top-1/2 -translate-y-1/2 text-theme-text-secondary hover:text-white text-xs"
                title="Clear"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Sort By */}
        <div className="flex items-center gap-2">
          <label className="text-xs text-theme-text-secondary">Sort:</label>
          <div className="flex items-center gap-1">
            <div className="relative">
              <select
                value={filters.sortBy}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  updateFilter('sortBy', e.target.value)
                }
                className="bg-theme-surface-hover border border-theme-border rounded px-2 py-2 sm:py-1 pr-6 text-sm text-white focus:outline-none focus:border-theme-primary"
              >
                <option value="default">Default (Rank)</option>
                <option value="odds">Best Odds</option>
                <option value="confidence">Confidence</option>
                <option value="legs">Most Legs</option>
              </select>
              {filters.sortBy !== 'default' && (
                <button
                  type="button"
                  onClick={() => updateFilter('sortBy', 'default')}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-theme-text-secondary hover:text-white text-xs"
                  title="Clear"
                >
                  ×
                </button>
              )}
            </div>
            {/* Sort Direction Toggle */}
            <button
              type="button"
              onClick={() =>
                updateFilter('sortDirection', filters.sortDirection === 'asc' ? 'desc' : 'asc')
              }
              className="p-2 rounded border border-theme-border bg-theme-surface-hover hover:bg-theme-surface-active text-theme-text-secondary hover:text-theme-text transition"
              title={`Sort ${filters.sortDirection === 'asc' ? 'ascending' : 'descending'}`}
            >
              {filters.sortDirection === 'asc' ? (
                <ArrowUp className="w-4 h-4" />
              ) : (
                <ArrowDown className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {/* Player Search */}
        <div className="flex items-center gap-2 flex-1 min-w-35 sm:min-w-50">
          <label className="text-xs text-theme-text-secondary">Player:</label>
          <div className="relative flex-1">
            <input
              type="text"
              value={filters.playerSearch}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateFilter('playerSearch', e.target.value)
              }
              placeholder="Search player..."
              className="w-full bg-theme-surface-hover border border-theme-border rounded px-2 py-2 sm:py-1 pr-6 text-sm text-white placeholder-theme-text-tertiary focus:outline-none focus:border-theme-primary"
            />
            {filters.playerSearch && (
              <button
                type="button"
                onClick={() => updateFilter('playerSearch', '')}
                className="absolute right-1 top-1/2 -translate-y-1/2 text-theme-text-secondary hover:text-white text-xs"
                title="Clear"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Quick Stats Lookup */}
        {onQuickStatsClick && (
          <button
            type="button"
            onClick={onQuickStatsClick}
            className="flex items-center gap-1.5 text-xs text-theme-primary hover:text-theme-primary-hover px-2 py-1 rounded hover:bg-theme-primary/20 transition border border-theme-primary/50"
            title="Look up player or team stats"
          >
            <Search className="w-3.5 h-3.5" />
            Stats
          </button>
        )}

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs text-theme-danger hover:text-theme-danger/80 px-2 py-1 rounded hover:bg-theme-danger/20 transition"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between text-xs text-theme-text-secondary">
        <span>
          Showing {filteredCount} of {totalCount} parlays
          {hasActiveFilters && filteredCount === 0 && (
            <span className="text-theme-warning ml-2">— No parlays match your filters</span>
          )}
        </span>
        {hasActiveFilters && (
          <div className="flex items-center gap-2">
            {filters.confidence !== 'all' && (
              <span className="bg-theme-primary/50 text-theme-primary px-2 py-0.5 rounded-full">
                {filters.confidence} confidence
              </span>
            )}
            {filters.propType !== 'all' && (
              <span className="bg-purple-500/50 text-purple-400 px-2 py-0.5 rounded-full">
                {getCategoryLabel(filters.propType)}
              </span>
            )}
            {filters.playerSearch && (
              <span className="bg-theme-success/50 text-theme-success px-2 py-0.5 rounded-full">
                "{filters.playerSearch}"
              </span>
            )}
            {filters.sortBy !== 'default' && (
              <span className="bg-theme-warning/50 text-theme-warning px-2 py-0.5 rounded-full">
                sorted by {filters.sortBy}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
